import { svgIconPaths } from '@/assets/svg/icons';
import SvgIcon from '@/components/atoms/SvgIcon';
import ConfirmationModal from '@/components/molecules/ConfirmationModal';
import CustomTable from '@/components/molecules/CustomTable';
import ModalContainer from '@/components/molecules/ModalContainer/ModalContainer';
import LoadingPage from '@/components/templates/LoadingPage';
import SidebarItemViewTemplate from '@/components/templates/SidebarItemViewTemplate';
import { useDeleteOrganizationLead } from '@/hooks/organization-leads/useDeleteOrganizationLead';
import { useOrganizationLeads } from '@/hooks/organization-leads/useOrganizationLeads';
import { useTableColumnSort } from '@/hooks/useTableColumnSort';
import colors from '@/theme/colors';
import { TOrganizationLead } from '@/types/OrganizationLeads.types';
import { Box, HStack, Text, Tooltip, useDisclosure, VStack } from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';
import { format, differenceInDays, formatDistanceToNow } from 'date-fns';
import { useMemo, useState } from 'react';

const COLUMNS = ['Slug', 'Website', 'Created', 'Total # of Submissions', 'Action'] as const;
export type TOrganizationLeadsSortingColumn = (typeof COLUMNS)[number];

const MONTH_IN_DAYS = 30;

const OrganizationLeadsPage = () => {
  const { isOpen: isDeleteModalOpen, onClose: onCloseDeleteModal, onOpen: onOpenDeleteModal } = useDisclosure();
  const { isOpen: isDisplayModalOpen, onClose: onCloseDisplayModal, onOpen: onOpenDisplayModal } = useDisclosure();

  const { sortingColumn, sortingDirection, updateSortingColumn } = useTableColumnSort<TOrganizationLeadsSortingColumn>({
    defaultSortingColumn: 'Created',
  });

  const {
    isLoading,
    data: organizationsResponse,
    pagination,
    refetch,
    isRefetching,
  } = useOrganizationLeads({
    sortingColumn,
    sortingDirection,
  });

  const [orgLeadSlugToDelete, setOrgLeadSlugToDelete] = useState('');
  const [orgLeadToDisplay, setOrgLeadToDisplay] = useState<TOrganizationLead>();

  const organizationsTableData = organizationsResponse?.map((organizationLead) => ({
    ...organizationLead,
    id: organizationLead.created_at,
  }));

  const { mutate, isPending } = useDeleteOrganizationLead(() => {
    refetch();
    onCloseDeleteModal();
    setOrgLeadSlugToDelete('');
  });

  const deleteOrgLead = () => {
    mutate(orgLeadSlugToDelete);
  };

  const data = { nodes: organizationsTableData ?? [] };

  const tableListItems = useMemo(
    () =>
      data.nodes.map((item: (typeof data.nodes)[number]) => {
        const isExpired = differenceInDays(new Date(), new Date(item.created_at)) >= MONTH_IN_DAYS;

        return {
          id: item.slug,
          cells: [
            item.slug,
            item.website_url ?? '',
            <Text fontWeight={'bold'}>
              {format(new Date(item.created_at), 'MMM dd. h:mm a')},{' '}
              {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
            </Text>,
            <Text marginLeft={'70px'} fontWeight={'bold'}>
              {item.submission_count}
            </Text>,
            <Tooltip label={isExpired ? undefined : 'This organization lead is not expired yet, expires after 30 days'}>
              <Box
                paddingX={'16px'}
                cursor={isExpired ? 'pointer' : 'not-allowed'}
                onClick={(e) => {
                  if (isExpired) {
                    setOrgLeadSlugToDelete(item.slug);
                    onOpenDeleteModal();
                  }

                  e.stopPropagation();
                }}
              >
                <SvgIcon
                  style={{ cursor: isExpired ? 'pointer' : 'not-allowed' }}
                  iconPath={svgIconPaths.trashCan}
                  color={colors.extra.red}
                />
              </Box>
            </Tooltip>,
          ],
        };
      }),
    [data.nodes],
  );

  if (isLoading) return <LoadingPage />;

  return (
    <SidebarItemViewTemplate title={'Organizations'}>
      <VStack width={'100%'} marginTop={'20px'}>
        <CustomTable<TOrganizationLead[], TOrganizationLeadsSortingColumn>
          isLoading={isLoading}
          dataWithId={data.nodes}
          isRefetching={isRefetching}
          pagination={pagination}
          tableListItems={tableListItems}
          isSearching={false}
          noItemsMessage="No organizations created yet"
          columnsToSortBy={['Created', 'Total # of Submissions']}
          columns={COLUMNS}
          sortingColumn={sortingColumn}
          updateSortingColumn={updateSortingColumn}
          hasRowSelect
          navigateOnRowSelect={false}
          onRowSelect={(slug: any) => {
            const leadToDisplay = data.nodes.find((orgLead) => orgLead.slug === slug);
            setOrgLeadToDisplay(leadToDisplay);
            onOpenDisplayModal();
          }}
          sortingDirection={sortingDirection}
          fullWidth
          marginTop={'8px'}
        />
      </VStack>
      <ModalContainer isOpen={isDisplayModalOpen} onClose={onCloseDisplayModal} title="Organization Lead">
        <VStack alignItems={'start'}>
          <HStack>
            <Text fontWeight={'bold'}>Slug:</Text>
            <Text>{orgLeadToDisplay?.slug}</Text>
          </HStack>
          <HStack>
            <Text fontWeight={'bold'}>URL:</Text>
            <Text>{orgLeadToDisplay?.website_url}</Text>
          </HStack>
          <HStack>
            <Text fontWeight={'bold'}>Created at:</Text>
            <Text>{format(new Date(orgLeadToDisplay?.created_at ?? 0), 'MMM dd. h:mm a')}</Text>
          </HStack>
          <HStack>
            <Text fontWeight={'bold'}>Submissions:</Text>
            <Text>{orgLeadToDisplay?.submission_count}</Text>
          </HStack>
        </VStack>
      </ModalContainer>

      <ConfirmationModal
        confirmButtonText="Delete"
        title="Delete Organization Lead"
        description={`This will delete organization lead ${orgLeadSlugToDelete} and all submission data related to it`}
        confirmButtonColor="extra.red"
        onConfirm={deleteOrgLead}
        isOpen={isDeleteModalOpen}
        confirmLoading={isPending}
        onCancel={onCloseDeleteModal}
      />
    </SidebarItemViewTemplate>
  );
};

export const Route = createFileRoute('/_authenticated/_admin/organization-leads/')({
  component: OrganizationLeadsPage,
});
